import { useState, useEffect } from 'react'
import { Divider } from 'semantic-ui-react'
import Color from 'color'
import { castArray } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@/hooks'
import Popup from '@/ui/Popup'
import { Icon, Text } from '..'
import { MenuItemWrapper, StyledSegment, MenuTitle } from './Styles'
import { MenuItemProps, PopupMenuProps } from './types'

const MenuItem = ({
  active,
  disabled,
  disabledReason,
  title,
  onClick,
  highlighted,
}: MenuItemProps) => (
  <Popup
    content={disabledReason}
    position="left center"
    disabled={!disabled || !disabledReason}
    inverted
    trigger={
      <div>
        <MenuItemWrapper
          noMargin
          active={active}
          disabled={disabled}
          highlighted={highlighted}
          onClick={onClick}
          justifyContent="flex-start">
          <Text.Span light>{title}</Text.Span>
        </MenuItemWrapper>
      </div>
    }
  />
)

const PopupMenu = ({
  className,
  iconClassName,
  items = [],
  menuTitle,
  customTrigger,
  customMenuItem,
  disabled,
  style,
  basic,
  flowing,
  active,
  icon = 'icon-dots02',
  position = 'top left',
  maxHeight,
}: PopupMenuProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [itemsToDisplay, setItemsToDisplay] = useState(items)
  const theme = useTheme()

  useEffect(() => {
    if (!isOpen) {
      setItemsToDisplay(items)
    }
  }, [isOpen, items])

  const MenuChild = customMenuItem ? customMenuItem : MenuItem

  return (
    <Popup
      flowing={flowing}
      disabled={disabled}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      on="click"
      position={position}
      basic={basic}
      trigger={
        customTrigger ? (
          <div
            style={{
              display: 'inline-block',
            }}>
            {customTrigger}
          </div>
        ) : (
          <Icon
            button
            className={iconClassName}
            disabled={disabled}
            style={{
              marginRight: 0,
              ...(style ?? {}),
            }}
            onClick={e => e.stopPropagation()}
            name={icon}
            size="big"
            color={Color(theme.colors.abyss).fade(0.6).string()}
          />
        )
      }
      content={
        <StyledSegment
          menuTitle={menuTitle}
          className={className}
          basic
          maxHeight={maxHeight}>
          {menuTitle && <MenuTitle>{menuTitle}</MenuTitle>}

          {(itemsToDisplay || []).length === 0 && (
            <Text.Span light grey>
              {t('No options available')}
            </Text.Span>
          )}

          {itemsToDisplay.map(
            (
              {
                disabled,
                disabledReason,
                title,
                onClick,
                isDivider,
                highlighted,
                subItems,
              },
              i,
            ) =>
              isDivider ? (
                <Divider key={i} style={{ margin: '10px 0' }} />
              ) : (
                <MenuChild
                  key={i}
                  active={castArray(active).includes(i)}
                  disabled={disabled}
                  disabledReason={disabledReason}
                  title={title}
                  highlighted={highlighted}
                  onClick={
                    !disabled && (onClick || !!subItems)
                      ? e => {
                          if (subItems) {
                            setItemsToDisplay(subItems)
                          } else {
                            setIsOpen(false)

                            // Hooks do not support a second callback argument when setting state,
                            // so we use a good ol' setTimeout hack for calling onClick synchronously after setIsOpen.
                            // ... :(
                            setTimeout(() => onClick(e))
                          }
                        }
                      : undefined
                  }
                />
              ),
          )}
        </StyledSegment>
      }
    />
  )
}

export default PopupMenu
